import { useState, useEffect, useMemo } from 'react';
import { 
  X, CheckCircle, Settings, Layout, AlertCircle,
} from 'lucide-react';
import { Tutee } from '../../../types/tuition';
import {
  fetchDashboardComponents,
  fetchTuteeComponents,
  assignComponentToTutee,
  removeComponentFromTutee,
  checkComponentContent,
  DashboardComponent,
  TuteeComponent,
} from '../../../services/componentService';
import Select from '../../../components/ui/Select';
import { SelectOption } from '../../../components/ui/Select';
import AnimatedCard from '../../../components/ui/AnimatedCard';
import ConfirmationModal from '../../../components/ui/ConfirmationModal';

interface ComponentManagerProps {
  tutees: Tutee[];
  selectedTuteeId: string;
  onUpdate?: () => void;
}

const ComponentManager = ({ tutees, selectedTuteeId, onUpdate }: ComponentManagerProps) => {
  const [availableComponents, setAvailableComponents] = useState<DashboardComponent[]>([]);
  const [assignedComponents, setAssignedComponents] = useState<TuteeComponent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isRemoving, setIsRemoving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<{
    isOpen: boolean;
    tuteeComponentId: string;
    displayName: string;
    summary: string;
  }>({
    isOpen: false,
    tuteeComponentId: '',
    displayName: '',
    summary: '',
  });

  const selectedTutee = useMemo(() => tutees.find(t => t.id === selectedTuteeId), [tutees, selectedTuteeId]);

  useEffect(() => {
    loadData();
  }, [selectedTuteeId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const [components, assigned] = await Promise.all([
        fetchDashboardComponents(),
        selectedTuteeId ? fetchTuteeComponents(selectedTuteeId) : Promise.resolve([]),
      ]);
      setAvailableComponents(components);
      setAssignedComponents(assigned);
    } catch (err) {
      setError('Failed to load components. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async (componentId: string) => {
    if (!selectedTuteeId) return;
    try {
      setError('');
      await assignComponentToTutee(selectedTuteeId, componentId, assignedComponents.length);
      await loadData();
      onUpdate?.();
    } catch (err) {
      setError('Failed to assign component. Please try again.');
      console.error(err);
    }
  };

  const handleRemove = async (tuteeComponent: TuteeComponent) => {
    if (!selectedTuteeId) return;

    try {
      setError('');
      
      // Check for existing content
      const { hasContent, summary } = await checkComponentContent(
        selectedTuteeId, 
        tuteeComponent.component?.name || ''
      );

      if (hasContent) {
        setConfirmDelete({
          isOpen: true,
          tuteeComponentId: tuteeComponent.id,
          displayName: tuteeComponent.component?.displayName || 'Module',
          summary: summary,
        });
        return;
      }

      // If no content, remove directly
      setIsRemoving(true);
      await removeComponentFromTutee(tuteeComponent.id);
      await loadData();
      onUpdate?.();
    } catch (err) {
      setError('Failed to remove component. Please try again.');
      console.error(err);
    } finally {
      setIsRemoving(false);
    }
  };

  const confirmRemoval = async () => {
    if (!confirmDelete.tuteeComponentId) return;

    try {
      setIsRemoving(true);
      setError('');
      await removeComponentFromTutee(confirmDelete.tuteeComponentId);
      await loadData();
      onUpdate?.();
      setConfirmDelete(prev => ({ ...prev, isOpen: false }));
    } catch (err) {
      setError('Failed to remove component. Please try again.');
      console.error(err);
    } finally {
      setIsRemoving(false);
    }
  };

  const unassignedComponents = availableComponents.filter(
    (comp) => !assignedComponents.some((ac) => ac.componentId === comp.id)
  );

  const componentOptions: SelectOption[] = unassignedComponents.map((comp) => ({
    value: comp.id,
    label: comp.displayName,
  }));

  return (
    <AnimatedCard className="overflow-hidden !p-0">
      <div className="p-4 sm:p-6">
        {loading ? (
          <div className="py-12 flex flex-col items-center justify-center gap-4 text-gray-400">
            <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
            <p>Loading modules...</p>
          </div>
        ) : !selectedTuteeId ? (
          <div className="py-12 text-center text-gray-500 bg-white rounded-xl border-2 border-dashed border-gray-100">
            <p>Please select a tuition group to manage their modules</p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Add Component */}
            <div>
              <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">Add Available Module</h3>
              {unassignedComponents.length === 0 ? (
                <div className="p-4 bg-green-50 text-green-700 rounded-xl border border-green-100 text-sm flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  All available modules are already assigned to this group.
                </div>
              ) : (
                <div className="flex gap-3">
                  <div className="flex-1 max-w-md">
                    <Select
                      value=""
                      onChange={(value) => value && handleAssign(value)}
                      options={componentOptions}
                      placeholder="Select a module to add..."
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Assigned Components */}
            <div>
              <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">
                Assigned Modules for {selectedTutee?.name} ({assignedComponents.length})
              </h3>
              {assignedComponents.length === 0 ? (
                <div className="py-12 text-center text-gray-400 bg-white rounded-xl border-2 border-dashed border-gray-100">
                  <Layout className="w-12 h-12 mx-auto mb-3 opacity-20" />
                  <p>No modules assigned to this group yet.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
                  {assignedComponents.map((tuteeComp) => (
                    <div
                      key={tuteeComp.id}
                      className="group p-3 sm:p-4 bg-white border border-gray-100 rounded-xl sm:rounded-2xl flex items-center justify-between hover:shadow-lg hover:border-indigo-100 transition-all"
                    >
                      <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                        <div className="p-1.5 sm:p-2 bg-indigo-50 text-indigo-600 rounded-lg group-hover:scale-110 transition-transform flex-shrink-0">
                          <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <span className="font-bold text-gray-800 block text-sm sm:text-base truncate">
                            {tuteeComp.component?.displayName || 'Unknown Module'}
                          </span>
                          {tuteeComp.component?.description && (
                            <span className="text-[10px] sm:text-xs text-gray-500 block truncate">
                          {tuteeComp.component.description}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => {
                        // Scroll to the respective admin section if it exists
                        const sectionMap: Record<string, string> = {
                          'learning_points': 'feedback-admin-section',
                          'worksheet_tracker': 'module-manager-section',
                          'spelling_quiz': 'spelling-quiz-admin-section',
                          'chemistry_quiz': 'module-manager-section',
                          'hanzi_hero': 'hanzi-hero-admin-section',
                          'flashcards': 'flashcards-admin-section',
                          'shared_files': 'global-file-admin-section',
                        };
                        const sectionId = sectionMap[tuteeComp.component?.name || ''];
                        if (sectionId) {
                          document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
                        } else {
                          // Handle other modules (like worksheet_tracker)
                          // For now, we'll just show an alert or we could expand a configuration area
                          alert(`Configuration for ${tuteeComp.component?.displayName} is handled within the module on the tutee's dashboard.`);
                        }
                      }}
                      className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all"
                      title="Configure module"
                    >
                      <Settings className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleRemove(tuteeComp)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all opacity-0 group-hover:opacity-100"
                      title="Remove module"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                  ))}
                </div>
              )}
            </div>

            {error && (
              <div className="p-4 bg-red-50 border border-red-100 rounded-xl flex items-center gap-3 text-red-600 text-sm">
                <AlertCircle className="w-5 h-5" />
                <p>{error}</p>
              </div>
            )}
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={confirmDelete.isOpen}
        onClose={() => setConfirmDelete(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmRemoval}
        title={`Remove ${confirmDelete.displayName}?`}
        message={
          <div className="space-y-4">
            <div className="p-4 bg-amber-50 border-2 border-amber-200 rounded-2xl flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-amber-800">Existing Content Found!</p>
                <p className="text-xs text-amber-700 mt-1">
                  This module currently contains <span className="font-bold underline">{confirmDelete.summary}</span> for {selectedTutee?.name}.
                </p>
              </div>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed">
              Removing this module will hide it from the dashboard. The data will remain in the database, but the student will no longer be able to access it.
            </p>
            <div className="bg-gray-50 p-3 rounded-xl border border-gray-100">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest text-center">
                Are you sure you want to proceed?
              </p>
            </div>
          </div>
        }
        confirmText="Yes, Remove Module"
        cancelText="No, Keep It"
        type="warning"
        isLoading={isRemoving}
      />
    </AnimatedCard>
  );
};

export default ComponentManager;
